import { Input, Progress } from 'antd';
import React, { useState } from 'react';
import { useRecoilState } from 'recoil';

import { calculatePasswordStrength } from '../../Auth/SignUp';
import ButtonDefault, { ButtonVariants } from '../../shared/basic/button';
import { ShouldRender } from '../../shared/basic/ShouldRender';
import { formateErrorAppwriteObject } from '../../shared/formateErrorObject';
import { notify } from '../../shared/notify';
import { getStrengthText } from '../../V2/SignUpModal';
import { user } from '../../../atoms/user';
import appwriteService from '../../../config/appwrite';
import { User } from '../../../utils/interfaces/User';

const ProfileSecurity = () => {
  const [loading, setLoading] = useState(false);
  const [userData] = useRecoilState<User | undefined>(user);
  const [passwordStrength, setPasswordStrength] = useState<number>(0);

  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    cnfNewPassword: '',
  });

  const [errors, setErrors] = useState({
    oldPassword: '',
    newPassword: '',
    cnfNewPassword: '',
  });

  const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(true);

  const validatePassword = (password: string) => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    return passwordRegex.test(password);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Validate password strength
    if (name === 'newPassword') {
      const isPasswordValid = validatePassword(value);

      const strength = calculatePasswordStrength(value);
      setPasswordStrength(strength);

      if (!isPasswordValid) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          newPassword: 'Password does not meet the criteria.',
        }));
        setIsUpdateButtonDisabled(true);
      } else {
        setErrors((prevErrors) => ({ ...prevErrors, newPassword: '' }));
        setIsUpdateButtonDisabled(
          value === '' || formData.cnfNewPassword === '' || !isPasswordValid,
        );
      }
    }

    // Validate password match
    if (name === 'cnfNewPassword') {
      if (value !== formData.newPassword) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          cnfNewPassword: 'Passwords do not match.',
        }));
        setIsUpdateButtonDisabled(true);
      } else {
        setErrors((prevErrors) => ({ ...prevErrors, cnfNewPassword: '' }));
        setIsUpdateButtonDisabled(value === '' || formData.newPassword === '');
      }
    }

    if (name === 'oldPassword') {
      setIsUpdateButtonDisabled(
        value === '' ||
          formData.newPassword === '' ||
          formData.cnfNewPassword === '',
      );
    }
  };

  const handleSubmitReset = async () => {
    try {
      setLoading(true);

      await appwriteService.updatePassword(
        formData?.newPassword,
        formData?.oldPassword,
      );

      notify('Password reset successfully!', 'success');
      setIsUpdateButtonDisabled(false);
    } catch (error: any) {
      const message = formateErrorAppwriteObject(error);

      notify(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className='flex flex-col gap-4'>
        <div className='col-span-1 flex-col flex gap-y-.5'>
          <label className='text-base md:text-base text-textGrayProfile  pb-2 l flex gap-x-2'>
            Email Id
          </label>
          <span className='text-base md:text-xl font-semibold text-[#9b9b9b]'>
            {userData?.email}
          </span>
        </div>
        <div className='col-span-1 flex-col flex gap-y-.5'>
          <label className='text-sm  md:text-base text-textGrayProfile pb-2 l flex gap-x-2'>
            Current Password <p className='text-red-500'>*</p>{' '}
          </label>
          <Input.Password
            type='password'
            name='oldPassword'
            autoComplete='current-password'
            autoFocus={false}
            placeholder='Current Password'
            value={formData.oldPassword}
            onChange={handleChange}
            size='large'
            className=' placeholder:text-textDarkGrayColor rounded-lg border-bgDarkGray    text-white '
          />

          {errors.oldPassword && (
            <p className='text-red-500'>{errors.oldPassword}</p>
          )}
        </div>

        <div className='col-span-1 flex-col flex gap-y-.5'>
          <label className='text-sm  md:text-base text-textGrayProfile pb-2 l flex gap-x-2'>
            New Password
          </label>
          <Input.Password
            name='newPassword'
            type='password'
            autoComplete='current-password'
            value={formData.newPassword}
            placeholder='New Password'
            autoFocus={false}
            onChange={handleChange}
            size='large'
            className=' placeholder:text-textDarkGrayColor rounded-lg border-bgDarkGray  text-white '
          />
          {errors.newPassword && (
            <p
              className={`text-${isUpdateButtonDisabled ? 'red-500' : 'white'}`}
            >
              {errors.newPassword}
            </p>
          )}
          <ShouldRender check={formData.newPassword}>
            {passwordStrength !== null && (
              <span className='relative'>
                <Progress
                  percent={(passwordStrength / 5) * 100}
                  showInfo={false}
                  status={passwordStrength >= 3 ? 'success' : 'exception'}
                />
                <p
                  className={`mt-2 text-${
                    passwordStrength >= 3 ? 'green' : 'red'
                  }-500 absolute text-xs -bottom-3 font-medium`}
                >
                  {getStrengthText(passwordStrength)}
                </p>
              </span>
            )}
          </ShouldRender>
          <span className='flex flex-col text-sm font-medium pt-2 '>
            <p className='text-[#7a7a7a]'>Minimum length: 8 characters</p>
            <p className='text-[#7a7a7a]'>
              Include at least one uppercase, one lowercase, and one numeric
              character
            </p>
          </span>
        </div>

        <div className='col-span-1 flex-col flex gap-y-.5'>
          <label className='text-sm  md:text-base text-textGrayProfile pb-2 l flex gap-x-2'>
            Confirm New Password
          </label>
          <Input.Password
            name='cnfNewPassword'
            type='password'
            autoComplete='current-password'
            placeholder='Confirm New Password'
            value={formData.cnfNewPassword}
            onChange={handleChange}
            size='large'
            className=' placeholder:text-textDarkGrayColor rounded-lg border-bgDarkGray    text-white '
          />
          {errors.cnfNewPassword && (
            <p className='text-red-500'>{errors.cnfNewPassword}</p>
          )}
        </div>

        <div className='col-span-1 mt-4 flex-col flex gap-y-.5 p-1'>
          <ButtonDefault
            size={1}
            variant={ButtonVariants.UNSTYLED}
            className='w-full md:w-min p-1 font-semibold rounded bg-fadedWhite disabled:cursor-not-allowed hover:bg-gray-100 text-black disabled:bg-dropDownMenuBg disabled:text-[#3E3E3E] disabled:border-0'
            loading={loading}
            disabled={isUpdateButtonDisabled}
            onClick={handleSubmitReset}
          >
            <span className='flex text-base gap-x-2 px-4 whitespace-nowrap disabled:cursor-not-allowed'>
              Update
            </span>
          </ButtonDefault>
        </div>

        {/* <div className='border-b border-gray-800 w-full pt-8'></div>

        <div className='col-span-1 mt-4 flex-col flex gap-y-4'>
          <span className='text-textGrayProfile font-bold text-base md:text-xl'>
            Close Account
          </span>
          <div className='flex flex-col gap-2 text-textGrayProfile '>
            <div className='flex flex-row gap-2'>
              <Checkbox onChange={handleCheckboxChange}></Checkbox>
              <span className='text-sm md:text-base'>
                You understand your account can't be recover
              </span>
            </div>
            <div>
              <span className='font-semibold underline text-sm md:text-base text-yellow-600'>
                Warning
              </span>
              <span className='text-xs md:text-sm'>
                : If you close your account you will be unsubscribed from all
                your courses and will lose access forever.
              </span>
            </div>
          </div>
          <div className='col-span-1 mt-4 flex-col flex justify-end items-end md:justify-start md:items-start gap-y-.5'>
            <ButtonDefault
              size={1}
              variant={ButtonVariants.UNSTYLED}
              className='w-full md:w-min disabled:cursor-not-allowed rounded bg-fadedWhite hover:bg-gray-100 text-black p-3 disabled:bg-dropDownMenuBg disabled:text-[#3E3E3E] disabled:border-0'
              loading={loading}
              disabled={!isChecked}
            >
              <span className='flex text-base  gap-x-2 px-4 font-bold whitespace-nowrap'>
                Close Account
              </span>
            </ButtonDefault>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default ProfileSecurity;
